/**
 * The site's lift write gate, and the only place in any console where
 * it can be armed.
 *
 * There was no arming affordance anywhere before this — not in this
 * SPA, not in the support console — so when Hotel NoMad's six lifts
 * all read "Fault" because the cloud believed the bridge was disarmed,
 * the only surface that could have fixed it was a curl against the
 * edge box's own `POST /api/writes/arm`, on a machine on the hotel's
 * LAN.
 *
 * SITE-WIDE, and the copy says so on every screen it appears on. The
 * bridge holds one gate for the whole site (invariant I6), so arming
 * from a panel that happens to be inside one lift's drawer still arms
 * all of them, and an operator who did not know that would be arming
 * six guest elevators believing they had armed one.
 */
import { useState } from 'react';
import { StatusChip } from '../../design-system/components/StatusChip';
import { ConfirmDialog } from '../../design-system/components/ConfirmDialog';
import { RoleGuard } from '../../auth/RoleGuard';
import { LIFT_ARM_WRITES_ROLES } from '../../auth/types';
import { useActiveSite } from '../../state/useActiveSite';
import { useArmLiftWrites, useLiftWriteGate } from '../../api/hooks/useLifts';
import { armBlockedReason, gateIsReadable, writeGateStatus } from './lift-dispatch-status';
import './LiftWriteGatePanel.css';

/** One row of the WS8 list, as the 409 detail carries it. */
interface OutstandingTask {
  readonly task_id: string;
  readonly state?: string;
  readonly label?: string;
  readonly robot?: string;
  readonly lifts?: string[];
  readonly evidence?: string[];
  readonly disarm_class?: string;
}

interface DisarmOutstanding {
  readonly code: string;
  readonly message: string;
  readonly tasks: OutstandingTask[];
  readonly inCar: OutstandingTask[];
}

/**
 * Turn the api-server's 409 into the acknowledgement step, or return
 * null for anything else.
 *
 * TWO CODES, ONE DIALOG. `lift_routed_tasks_outstanding` asks for a typed
 * reason; `stranding_not_acknowledged` asks for the checkbox as well. Both
 * are ACKNOWLEDGEMENTS -- the server's own body names the retry, and
 * invariant I2 says a disarm is never refused -- so both land here rather
 * than in the generic error line, which offers no way forward.
 */
function outstandingFromError(error: unknown): DisarmOutstanding | null {
  const body = (error as { body?: unknown } | null)?.body;
  const detail = (body as { detail?: unknown } | undefined)?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const code = (detail as { code?: unknown }).code;
  if (code !== 'lift_routed_tasks_outstanding' && code !== 'stranding_not_acknowledged') {
    return null;
  }
  const inner = (detail as { detail?: unknown }).detail as
    | { tasks?: OutstandingTask[]; in_car?: OutstandingTask[] }
    | undefined;
  const inCar = Array.isArray(inner?.in_car) ? inner.in_car : [];
  const tasks = Array.isArray(inner?.tasks) ? inner.tasks : inCar;
  return {
    code,
    message:
      typeof (detail as { message?: unknown }).message === 'string'
        ? ((detail as { message: string }).message)
        : 'Lift-routed tasks are outstanding.',
    tasks,
    inCar,
  };
}

export function LiftWriteGatePanel() {
  const { site } = useActiveSite();
  const gateQuery = useLiftWriteGate();
  const arm = useArmLiftWrites();

  const gate = gateQuery.data;
  const status = writeGateStatus(gate);
  const [reason, setReason] = useState('');
  const [confirmingArm, setConfirmingArm] = useState(false);
  // -- WS8: the disarm acknowledgement -------------------------------
  //
  // THE OVERRIDE IS NEVER SENT ON THE FIRST PRESS. A disarm goes out
  // plain; if the api-server answers 409 with a list of lift-routed
  // tasks, THIS is what renders them and asks for a typed reason. That
  // ordering is the whole point: an override attached to every press
  // would make the guard invisible, which is the same as not having it.
  //
  // And it is an ACKNOWLEDGEMENT, not a gate. Every 409 the server can
  // raise names its own retry, and this panel must always render that
  // path -- invariant I2 says a disarm is never refused.
  const [outstanding, setOutstanding] = useState<DisarmOutstanding | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [strandAcknowledged, setStrandAcknowledged] = useState(false);

  const siteLabel = site?.label ?? 'this site';
  // Which control to draw follows the RAW flag, not the verdict, and
  // the asymmetry is deliberate. An armed bridge whose later inventory
  // failed reads as 'unvalidated' — tier 1 is refusing everything —
  // but it is still armed, and a supervisor must always be able to
  // stand it down. Making it harder to stop than to start would be
  // backwards (invariant I2), and that includes hiding the button.
  const isArmed = gate?.writes_armed === true;
  // Tier 1 gates tier 2 (invariant I3), and the bridge enforces it
  // anyway — this only stops the console offering a button whose one
  // possible outcome is a NACK reading "inventory not validated".
  //
  // Derived from the SAME verdict the chip above renders, and via the
  // same call that produces the tooltip, so "why is this disabled" and
  // "what does the chip say" cannot answer differently. Note that an
  // unreadable gate leaves this false: there is no default-open path
  // here, because the console offering to arm six guest lifts on no
  // information is worse than it offering nothing at all.
  const blockedReason = armBlockedReason(status);
  const canArm = blockedReason === undefined;

  function send(armed: boolean, override?: { reason: string; stranding: boolean }) {
    const trimmed = reason.trim();
    arm.mutate({
      armed,
      // `confirmed` is attached ONLY when arming. Sending
      // `confirmed: false` on a disarm would be harmless today, but it
      // would put a flag on the wire that a future reader could start
      // checking, and a disarm that can be refused for a missing
      // confirmation is invariant I2 broken (making it harder to stop
      // than to start would be backwards).
      ...(armed ? { confirmed: true } : {}),
      ...(trimmed ? { reason: trimmed } : {}),
      // Disarm only, and only after the typed step. `LiftArmRequest`
      // rejects either field with `armed: true`, and an api-server that
      // is still 0.6.34 answers either with a 422 -- which is why the
      // SPA ships AFTER the api-server.
      ...(override && !armed
        ? {
            override_reason: override.reason,
            ...(override.stranding ? { override_acknowledges_stranding: true } : {}),
          }
        : {}),
      // `request_id` and `actor` are NOT sent: the api-server mints
      // both, and its request model forbids extras, so sending either
      // is a 422 rather than a silently ignored field.
      idempotency_key: crypto.randomUUID(),
    });
    setReason('');
  }

  function disarm(override?: { reason: string; stranding: boolean }) {
    setOutstanding(null);
    arm.reset();
    send(false, override);
  }

  const submitted = arm.data;

  // Derived from the mutation's own error rather than set in a callback:
  // the 409 can arrive from any press, including the override press
  // itself (the list may have changed between the GET and the POST), and
  // re-rendering from the latest body keeps the dialog honest about what
  // is outstanding NOW.
  const currentOutstanding = arm.isError ? outstandingFromError(arm.error) : null;
  if (currentOutstanding && currentOutstanding.message !== outstanding?.message) {
    setOutstanding(currentOutstanding);
  }

  const confirmArmDialog = (
    <ConfirmDialog
      open={confirmingArm}
      title="Arm dispatching writes?"
      message={`This lets robots call every lift at ${siteLabel} and put the cars into AGV mode. Nothing limits how many robots do so at once, and guests share these lifts. Disarming afterwards takes one click and no confirmation.`}
      confirmLabel="Arm"
      cancelLabel="Cancel"
      variant="danger"
      onConfirm={() => {
        setConfirmingArm(false);
        send(true);
      }}
      onCancel={() => setConfirmingArm(false)}
    />
  );

  return (
    <section className="nx-lift-gate" aria-label="Lift dispatching writes">
      <div className="nx-lift-gate__head">
        <div>
          <h3 className="nx-lift-gate__title">Dispatching writes</h3>
          <p className="nx-lift-gate__scope">
            Site-wide — this covers every lift at {siteLabel}, not one car.
          </p>
        </div>
        <span className="nx-lift-gate__head-end">
          <span data-testid="lift-gate-state">
            <StatusChip status={status.chip} label={status.label} />
          </span>
        </span>
      </div>

      <p className="nx-lift-gate__meaning">{status.meaning}</p>
      <p className="nx-lift-gate__action">{status.action}</p>
      {status.detail && (
        <p className="nx-lift-gate__detail" data-testid="lift-gate-detail">
          Bridge reports: {status.detail}
        </p>
      )}

      <RoleGuard
        allowedRoles={[...LIFT_ARM_WRITES_ROLES]}
        fallback={
          <p className="nx-lift-gate__scope">
            Arming takes a site supervisor or a platform administrator.
          </p>
        }
      >
        <div className="nx-lift-gate__controls">
          <label className="nx-lift-gate__reason">
            Reason (optional, kept in the audit trail)
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isArmed ? 'e.g. guest event in the lobby' : 'e.g. commissioning run, lift 3'}
              data-testid="lift-arm-reason"
            />
          </label>

          {isArmed ? (
            <button
              type="button"
              className="nx-lift-gate__btn nx-lift-gate__btn--disarm"
              data-testid="lift-disarm-btn"
              disabled={arm.isPending}
              // No confirmation step, deliberately (invariant I2). The
              // edge box's own dashboard confirms before arming and
              // not before disarming, for the reason written into
              // `routes_api.py`: making it harder to stop than to
              // start would be backwards.
              onClick={() => disarm()}
            >
              {arm.isPending ? 'Disarming…' : 'Disarm dispatching writes'}
            </button>
          ) : (
            <button
              type="button"
              className="nx-lift-gate__btn nx-lift-gate__btn--arm"
              data-testid="lift-arm-btn"
              disabled={!canArm || arm.isPending}
              title={blockedReason}
              onClick={() => setConfirmingArm(true)}
            >
              {arm.isPending ? 'Arming…' : 'Arm dispatching writes'}
            </button>
          )}

          {arm.isError && !outstanding && (
            <p className="nx-lift-gate__error" role="alert">
              {arm.error instanceof Error ? arm.error.message : 'The request failed.'}
            </p>
          )}
          {outstanding && (
            <div
              className="nx-lift-gate__outstanding"
              data-testid="lift-disarm-outstanding"
              role="alert"
            >
              <strong>{outstanding.message}</strong>
              <ul data-testid="lift-disarm-task-list">
                {outstanding.tasks.map((t) => (
                  <li key={t.task_id} data-testid={`lift-disarm-task-${t.disarm_class}`}>
                    <code>{t.task_id}</code> — {t.label}
                    {t.lifts?.length ? ` · ${t.lifts.join(', ')}` : ''}
                    {t.evidence?.length ? ` · ${t.evidence.join(', ')}` : ''}
                    {t.state === 'interrupted'
                      ? ' — interrupted; re-arming lift writes resumes it.'
                      : ''}
                    <span className="nx-lift-gate__advice">
                      {t.disarm_class === 'in_car'
                        ? /* NO "cancel it first" for an in-car row, at
                             either UI. Cancelling a task in a car leaves
                             the robot parked inside holding the lift --
                             the advice that closes challenge S7 is the
                             stranding sentence, not a cancel. */
                          ` ${t.robot ?? 'The robot'} is in or boarding ${
                            t.lifts?.[0] ?? 'the lift'
                          }: disarming now strands it in the car. Wait for it to exit, or use the lift procedure (Recover).`
                        : ' Cancel it from the task controls.'}
                    </span>
                  </li>
                ))}
              </ul>
              <label className="nx-lift-gate__reason">
                Why disarm anyway? (at least 10 characters, kept in the audit trail)
                <input
                  type="text"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  data-testid="lift-disarm-override-reason"
                />
              </label>
              {outstanding.inCar.length > 0 && (
                <label className="nx-lift-gate__ack" data-testid="lift-disarm-strand-ack">
                  <input
                    type="checkbox"
                    checked={strandAcknowledged}
                    onChange={(e) => setStrandAcknowledged(e.target.checked)}
                  />
                  <span>
                    I understand {outstanding.inCar[0]?.robot ?? 'the robot'} will be stranded in{' '}
                    {outstanding.inCar[0]?.lifts?.[0] ?? 'the car'}.
                  </span>
                </label>
              )}
              <button
                type="button"
                className="nx-lift-gate__btn nx-lift-gate__btn--disarm"
                data-testid="lift-disarm-override-btn"
                disabled={
                  overrideReason.trim().length < 10 ||
                  (outstanding.inCar.length > 0 && !strandAcknowledged) ||
                  arm.isPending
                }
                onClick={() =>
                  disarm({
                    reason: overrideReason.trim(),
                    stranding: outstanding.inCar.length > 0 && strandAcknowledged,
                  })
                }
              >
                Disarm anyway
              </button>
            </div>
          )}
          {submitted && (
            <p className="nx-lift-gate__scope" data-testid="lift-arm-submitted" role="status">
              {/* ACKNOWLEDGED, never "Armed", and never "Confirmed".
                  All the api-server can tell us is that the request was
                  authorised and handed to the worker; the bridge
                  accepts or refuses on its own MQTT ack topic and
                  republishes its gate separately. Reporting a request
                  as though it were the live flag is the exact mistake
                  that put six lifts into MODE_OFFLINE.

                  And the refusal really does go nowhere near here. The
                  bridge NACKs with "confirmation required" or
                  "inventory not validated: <item>" onto
                  `site/response/ack`, which nothing on the read side
                  forwards to a browser — so an operator whose arm was
                  refused sees no error, anywhere, ever. The console
                  cannot fix that transport, but it can refuse to let
                  silence read as success, which is what the two
                  sentences below are for. */}
              {submitted.requested_armed ? 'Arm' : 'Disarm'} request {submitted.request_id}{' '}
              acknowledged — not confirmed.{' '}
              {/* THE GUARD DID NOT RUN, said out loud. Both of these
                  mean the disarm went through WITHOUT the lift-routed
                  check -- the safe direction, and exactly the thing a
                  person would otherwise assume had happened. */}
              {submitted.guard_status === 'list_unavailable' ||
              submitted.guard_status === 'bypassed_fire_mode' ? (
                <span data-testid="lift-arm-guard-status">
                  {submitted.guard_status === 'bypassed_fire_mode'
                    ? 'Fire mode is active, so the lift-routed task check did not run. '
                    : `The lift-routed task check did not run: ${
                        submitted.warnings?.[0] ?? 'the task list was unavailable.'
                      } `}
                </span>
              ) : null}
              {gateIsReadable(status)
                ? 'The bridge may still refuse it, and its refusal is delivered only to its own ack topic, never to this console. The state above is the only confirmation: if it does not change, treat the request as refused.'
                : 'Nothing here can tell you whether the bridge accepted it, because the gate itself cannot be read from this console. Confirm on the edge box before acting as though it took.'}
            </p>
          )}
        </div>
      </RoleGuard>

      {confirmArmDialog}
    </section>
  );
}
